// src/pages/currencies/ViewCurrency.tsx

import { useMemo, useState, useEffect } from "react";
import { FiPlus } from "react-icons/fi";

import AdminLayout from "@/layouts/AdminLayout";
import Breadcrumbs from "@/components/shared/nav/Breadcrumbs";
import Modal from "@/components/shared/ui/Modal";
import ConfirmDialog from "@/components/shared/ui/ConfirmDialog";
import Input from "@/components/shared/ui/Input";

import CurrenciesTable, {
  type CurrencyTableFilters,
  type CurrencyRow,
} from "@/components/currencies/CurrenciesTable";

import CreateCurrency, {
  type UpsertCurrency,
} from "@/components/currencies/forms/CreateCurrency";
import UpdateCurrency from "@/components/currencies/forms/UpdateCurrency";

import {
  useCurrenciesList,
  useCreateCurrencyMutation,
  useUpdateCurrencyMutation,
  useDeleteCurrencyMutation,
} from "@/api/features/currencies/";
import type { Currency as DtoCurrency } from "@/api/features/currencies/";

/* ------------------------------ Component --------------------------- */
export default function ViewCurrency() {
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState<null | DtoCurrency>(null);

  // Confirm dialog (delete)
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTitle, setConfirmTitle] = useState("Please confirm");
  const [confirmMsg, setConfirmMsg] = useState("");
  const [confirmCta, setConfirmCta] = useState("Confirm");
  const [confirmAction, setConfirmAction] = useState<() => void>(() => () => {});

  // Errors for mutations
  const [createError, setCreateError] = useState<string | null>(null);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Filters + global search
  const [filters, setFilters] = useState<CurrencyTableFilters>({});
  const [globalQ, setGlobalQ] = useState("");

  // Pagination (server-driven)
  const [page, setPage] = useState(1);
  const DEFAULT_PAGE_SIZE = 20;

  // Reset page when filters or global search change
  useEffect(() => setPage(1), [filters, globalQ]);

  // Build API query
  const q = useMemo(() => {
    const bits = [globalQ, filters.code, filters.name].filter(Boolean);
    return bits.length ? bits.join(" ").trim() : undefined;
  }, [globalQ, filters.code, filters.name]);

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
  } = useCurrenciesList({
    page,
    limit: DEFAULT_PAGE_SIZE,
    q,
    // includeDeleted: false by default
  });

  // Use server-returned pagination if present
  const total = data?.total ?? 0;
  const pageSize = data?.pageSize ?? DEFAULT_PAGE_SIZE;
  const currentPage = data?.page ?? page;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // Map API items → table rows (server already paginates)
  const pageRows: CurrencyRow[] = (data?.items ?? []).map((c) => ({
    id: c.id,
    code: c.code,
    name: c.name,
    symbol: c.symbol ?? null,
    minorUnit: c.minorUnit ?? null,
    country: c.country ?? null,
  }));

  /* ---------------------------- Mutations ---------------------------- */
  const createMut = useCreateCurrencyMutation();
  const updateMut = useUpdateCurrencyMutation(editOpen?.id ?? 0);
  const deleteMut = useDeleteCurrencyMutation();

  const onCreate = async (payload: UpsertCurrency) => {
    setCreateError(null);
    try {
      await createMut.mutateAsync({
        code: payload.code.trim().toUpperCase(),
        name: payload.name.trim(),
        symbol: payload.symbol?.trim() ? payload.symbol.trim() : null,
        minorUnit:
          payload.minorUnit === "" || payload.minorUnit == null
            ? null
            : Number(payload.minorUnit),
        country: payload.country?.trim() ? payload.country.trim() : null,
      });
      setCreateOpen(false);
      refetch();
    } catch (e: any) {
      setCreateError(e?.message || "Failed to create currency");
    }
  };

  const onEdit = async (payload: UpsertCurrency) => {
    if (!editOpen) return;
    setUpdateError(null);
    try {
      await updateMut.mutateAsync({
        code: payload.code?.trim()
          ? payload.code.trim().toUpperCase()
          : undefined,
        name: payload.name?.trim() ? payload.name.trim() : undefined,
        symbol: payload.symbol?.trim() ? payload.symbol.trim() : null,
        minorUnit:
          payload.minorUnit === "" || payload.minorUnit == null
            ? null
            : Number(payload.minorUnit),
        country: payload.country?.trim() ? payload.country.trim() : null,
      });
      setEditOpen(null);
      refetch();
    } catch (e: any) {
      setUpdateError(e?.message || "Failed to update currency");
    }
  };

  const askDelete = (c: CurrencyRow) => {
    setDeleteError(null);
    setConfirmTitle("Delete currency");
    setConfirmMsg(
      `Do you want to delete the currency “${c.code} — ${c.name}”? This action cannot be undone.`
    );
    setConfirmCta("Delete");
    setConfirmAction(() => async () => {
      try {
        await deleteMut.mutateAsync(Number(c.id));
        setConfirmOpen(false);
        refetch();
      } catch (e: any) {
        setDeleteError(e?.message || "Failed to delete currency");
      }
    });
    setConfirmOpen(true);
  };

  const serialStart = (currentPage - 1) * pageSize;

  return (
    <AdminLayout>
      {/* Header */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Currencies</h1>
          <div className="mt-1">
            <Breadcrumbs />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Search */}
          <div className="relative">
            <svg
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              viewBox="0 0 24 24"
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <Input
              value={globalQ}
              onChange={(e) => setGlobalQ(e.target.value)}
              placeholder="Search currencies…"
              className="w-56 pl-8"
            />
          </div>

          <button
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-sky-600 px-3 py-2 text-sm font-semibold text-white hover:bg-sky-700"
            onClick={() => setCreateOpen(true)}
          >
            <FiPlus /> Add Currency
          </button>
        </div>
      </div>

      {/* Error banners */}
      {isError && (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          Failed to load currencies: {(error as Error)?.message || "Unknown error"}
        </div>
      )}
      {createError && (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {createError}
        </div>
      )}
      {updateError && (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {updateError}
        </div>
      )}
      {deleteError && (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {deleteError}
        </div>
      )}

      {/* Table */}
      <CurrenciesTable
        rows={pageRows}
        pageRows={pageRows}
        serialStart={serialStart}
        filters={filters}
        setFilters={setFilters}
        onView={() => {
          // optional: open a view modal/page later
          // keeping parity with table action; we open edit for now if you prefer.
        }}
        onEdit={(r) => {
          const full = (data?.items ?? []).find((x) => x.id === r.id) as
            | DtoCurrency
            | undefined;
          if (full) setEditOpen(full);
        }}
        onDelete={askDelete}
      />

      {/* Pagination */}
      <div className="mt-0 flex items-center justify-between gap-2 rounded-b-2xl border border-t-0 border-gray-200 bg-white px-4 py-3">
        <div className="text-xs text-gray-500">
          {isLoading ? (
            <span>Loading…</span>
          ) : (
            <>
              Showing{" "}
              <span className="font-medium text-gray-700">
                {total === 0 ? 0 : serialStart + 1}
              </span>{" "}
              to{" "}
              <span className="font-medium text-gray-700">
                {Math.min(serialStart + pageSize, total)}
              </span>{" "}
              of{" "}
              <span className="font-medium text-gray-700">{total}</span> results
            </>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1 || isLoading}
            className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Prev
          </button>
          <div className="hidden items-center gap-1 sm:flex">
            {Array.from({ length: Math.min(totalPages, 6) }).map((_, idx) => {
              const n = idx + 1;
              const active = n === currentPage;
              return (
                <button
                  key={n}
                  onClick={() => setPage(n)}
                  className={`rounded-md px-2.5 py-1.5 text-sm ${
                    active
                      ? "bg-sky-600 text-white"
                      : "border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  {n}
                </button>
              );
            })}
            {totalPages > 6 && (
              <span className="px-1 text-sm text-gray-500">…</span>
            )}
          </div>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages || isLoading}
            className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>

      {/* Modals */}
      <Modal
        title="Create Currency"
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      >
        <CreateCurrency onSubmit={onCreate} onCancel={() => setCreateOpen(false)} />
      </Modal>

      <Modal title="Edit Currency" open={!!editOpen} onClose={() => setEditOpen(null)}>
        {editOpen ? (
          <UpdateCurrency
            currency={{
              id: editOpen.id,
              code: editOpen.code,
              name: editOpen.name,
              symbol: editOpen.symbol ?? "",
              minorUnit: editOpen.minorUnit ?? null,
              country: editOpen.country ?? "",
            }}
            onSubmit={onEdit}
            onCancel={() => setEditOpen(null)}
          />
        ) : null}
      </Modal>

      {/* Confirm delete */}
      <ConfirmDialog
        open={confirmOpen}
        title={confirmTitle}
        message={confirmMsg}
        confirmText={confirmCta}
        cancelText="Cancel"
        onCancel={() => setConfirmOpen(false)}
        onConfirm={confirmAction}
      />
    </AdminLayout>
  );
}
